// Visor de DOCUMENTOS (PDF) DENTRO de la app — el hermano de components/VisorImagen.jsx.
//
// POR QUÉ EXISTE (27-sep-2026, el dueño): «cuando envían un informe pdf por whatsapp, no hay manera de cerrarlo y volver
// a la app. Esto pasa en iphone». Las IMÁGENES de WhatsApp ya se abrían en su propia capa (VisorImagen, por la misma
// trampa), pero los documentos seguían con <a target="_blank">: dentro de la app del iPhone (Capacitor, que carga dentro
// de sí todo lo que es de biango.app) y en la app instalada en pantalla completa, el PDF SUSTITUYE a la aplicación, sin
// barra de direcciones ni botón de atrás, y hay que matar la app para salir.
//
// Aquí el PDF se pinta en una capa propia, página a página con pdf.js (el mismo motor, cargado solo al abrir uno: ver
// lib/pdfjsInit.js), y se cierra de las mismas maneras que la imagen: la X, la tecla Escape y el botón ATRÁS de Android
// (se mete una entrada en el historial al abrir; el back del sistema hace history.back() y aquí se cierra). La X va bajo
// el notch (safe-area). Lo que no es un PDF no se intenta pintar: se dice qué es y se ofrece compartirlo.
//
// COMPARTIR, Y NO «DESCARGAR», DENTRO DE LA APP. Un enlace de descarga en la app del iPhone vuelve a cargar el fichero
// en la misma vista —la trampa de la que se viene—. Dentro de la app se ofrece la hoja de compartir del sistema
// (guardar en Archivos, mandarlo por correo…) cuando el sistema la tiene; en el navegador, la descarga de siempre.
// (16-oct-2026) Y CUANDO LA APP NO LA TIENE, «DESCARGAR» POR EL NAVEGADOR DEL TELÉFONO. El dueño: «se abre el PDF pero no deja
// descargarlo», en la app del iPhone y en la de Android, y «lo mismo con los documentos en el chat de equipo». El botón sale
// siempre que hay fichero, y en la app sin hoja de compartir lo manda al navegador del teléfono (utils/descargaFuera).
// (17-oct-2026) EL VÍDEO Y EL AUDIO SE REPRODUCEN AQUÍ. Ahora llega aquí todo lo que se abre en la app (el chat interno también:
// utils/descargasEnLaApp). Un vídeo o una nota de voz se reproducen en el visor; si el teléfono no sabe con ese formato, se dice
// y queda «Descargar». Lo que no se puede ver (Word, Excel, zip…) lleva también el botón en el centro, para no buscarlo.
// (27-oct-2026, B-2254) Y EL PDF SE AMPLÍA CON DOS DEDOS. El dueño: «en iPhone, cuando se abre un PDF o documento del estilo,
// no se puede pellizcar la pantalla para aumentar o disminuir». Dentro de la app el pellizco de la página está apagado
// (Capacitor sin `zoomEnabled`, y `maximum-scale=1.0` de B-838: utils/pellizco.js lo cuenta entero), así que las páginas van
// dentro de una zona con su propio zoom (utils/zonaAmpliable.jsx): dos dedos, doble toque, Ctrl+rueda y los botones − / +.
// Al soltar, las páginas que se ven se vuelven a pintar con más píxeles (utils/paginasPdf.js), para que la letra no salga borrosa.
import { useEffect, useCallback, useRef, useState } from 'react'
import { initPdfjs } from '../lib/pdfjsInit'
import { descargarBlob } from '../utils/abrirArchivo'
import { enLaAppNativa as enLaApp } from '../utils/verDocumento'
import { descargarFueraDeLaApp } from '../utils/descargaFuera'
import { useZonaAmpliable, ControlesZoom } from '../utils/zonaAmpliable'
import { pintarPagina, vestirPagina, afinarPaginas, soltarLienzo, dprDe } from '../utils/paginasPdf'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Un PDF, por su tipo o por su nombre (WhatsApp a veces lo manda como «application/octet-stream»).
export const esPdf = (mime, nombre) => /\bpdf\b/i.test(String(mime || '')) || /\.pdf$/i.test(String(nombre || '').trim())
// Un informe de 300 páginas no se pinta entero en un teléfono: con estas se ve lo que importa, y el resto se comparte.
export const MAX_PAGINAS = 40
// Un vídeo o un audio, por su tipo; por su nombre solo si el tipo no dice nada («octet-stream»).
const sinTipo = (mime) => !mime || /octet-stream/i.test(String(mime))
export const esVideo = (mime, nombre) => /^video\//i.test(String(mime || '')) || (sinTipo(mime) && /\.(mp4|mov|m4v)$/i.test(String(nombre || '').trim()))
export const esAudio = (mime, nombre) => /^audio\//i.test(String(mime || '')) || (sinTipo(mime) && /\.(mp3|m4a|aac|ogg|oga|opus|wav)$/i.test(String(nombre || '').trim()))

export default function VisorDocumento({ src, nombre, mime, onClose }) {
  const [estado, setEstado] = useState('cargando')   // cargando | listo | video | audio | noPdf | error
  const [paginas, setPaginas] = useState(0)
  const [puedeCompartir, setPuedeCompartir] = useState(false)
  const [avisoDescarga, setAvisoDescarga] = useState(null)
  const contRef = useRef(null)
  const blobRef = useRef(null)
  // Igual que en VisorImagen: no llamar a history.back() si la entrada que metimos ya no está.
  const nuestraEntrada = useRef(false)
  // Las páginas pintadas ({ pagina, lienzo, proporcion, pintado, i }), para volver a pintarlas con más píxeles al ampliar.
  const paginasRef = useRef([])
  const turnoAfinado = useRef(0)
  const afinar = useCallback((zoom, zonaEl, anchoCss) => {
    const turno = ++turnoAfinado.current
    afinarPaginas({ paginas: paginasRef.current, zona: zonaEl, zoom, anchoCss, dpr: dprDe(window),
      sigue: () => turno === turnoAfinado.current, documento: typeof document !== 'undefined' ? document : null })
  }, [])
  // B-2254 · el zoom del visor: a lo ancho de la pantalla (sin pasar de 980 px en un ordenador), y de ahí hasta ×5.
  const zona = useZonaAmpliable({ anchoMax: 980, margen: 4, alAfinar: afinar, etiqueta: t('components.VisorDocumento.documento_pellizca_toca_dos_veces', 'Documento {{x}}: pellizca, toca dos veces o usa los botones para ampliar', { x: nombre || '' }) })

  const cerrar = useCallback(() => {
    if (nuestraEntrada.current) {
      nuestraEntrada.current = false
      try { window.history.back(); return } catch { /* sin historial utilizable: se cierra a mano */ }
    }
    onClose?.()
  }, [onClose])

  useEffect(() => {
    if (!src) return undefined
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); cerrar() } }
    const onPop = () => { nuestraEntrada.current = false; onClose?.() }
    try { window.history.pushState({ visorDocumento: true }, ''); nuestraEntrada.current = true } catch { /* se cierra con la X */ }
    window.addEventListener('keydown', onKey)
    window.addEventListener('popstate', onPop)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('popstate', onPop)
      document.body.style.overflow = overflow
      if (nuestraEntrada.current) { nuestraEntrada.current = false; try { window.history.back() } catch { /* nada */ } }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  // Traer el fichero y pintarlo. Una sola vez por documento; si se cierra a medias, se para y se suelta el worker.
  useEffect(() => {
    if (!src) return undefined
    let vivo = true
    let tarea = null
    setEstado('cargando'); setPaginas(0)
    // Otro documento: sin zoom y arriba del todo, y ningún afinado del anterior a medias.
    turnoAfinado.current++
    paginasRef.current = []
    zona.reiniciar()
    ;(async () => {
      try {
        const r = await fetch(src)
        if (!r.ok) throw new Error('HTTP ' + r.status)
        const blob = await r.blob()
        if (!vivo) return
        blobRef.current = blob
        try {
          const f = new File([blob], nombre || 'documento', { type: blob.type || mime || 'application/octet-stream' })
          setPuedeCompartir(!!(navigator.canShare && navigator.canShare({ files: [f] })))
        } catch { setPuedeCompartir(false) }
        const tipo = mime || blob.type
        if (esVideo(tipo, nombre)) { setEstado('video'); return }
        if (esAudio(tipo, nombre)) { setEstado('audio'); return }
        if (!esPdf(tipo, nombre)) { setEstado('noPdf'); return }
        const pdfjs = await initPdfjs()
        if (!vivo) return
        // Quien cierra en pdfjs-dist 6 es la TAREA de carga (ver components/FirmaCaja.jsx).
        tarea = pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()), isEvalSupported: false })
        const doc = await tarea.promise
        if (!vivo) return
        setPaginas(doc.numPages)
        // El ancho de una página «ajustada» es el de la zona ampliable (a lo ancho, sin pasar de 980 px); cada lienzo ocupa
        // el 100 % del contenido, así que al ampliar crece con él.
        const base = zona.anchoBaseActual()
        const ancho = Math.max(240, base > 1 ? base : 360)
        const dpr = dprDe(window)
        for (let i = 1; i <= Math.min(doc.numPages, MAX_PAGINAS) && vivo; i++) {
          const pagina = await doc.getPage(i)
          const { lienzo, proporcion } = await pintarPagina(pagina, Math.floor(ancho * dpr), document)
          if (!vivo || !contRef.current) { soltarLienzo(lienzo); break }
          vestirPagina(lienzo, i)
          contRef.current.appendChild(lienzo)
          paginasRef.current.push({ pagina, lienzo, proporcion, pintado: lienzo.width, i })
          if (i === 1) setEstado('listo')
        }
      } catch (e) {
        if (vivo) setEstado('error')
      }
    })()
    return () => {
      vivo = false
      turnoAfinado.current++
      try { if (tarea) tarea.destroy() } catch { /* nada */ }
      for (const p of paginasRef.current) soltarLienzo(p.lienzo)
      paginasRef.current = []
      if (contRef.current) contRef.current.textContent = ''
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, nombre, mime])

  if (!src) return null

  const compartir = async () => {
    const blob = blobRef.current
    if (!blob) return
    const f = new File([blob], nombre || 'documento', { type: blob.type || mime || 'application/octet-stream' })
    if (navigator.canShare && navigator.canShare({ files: [f] })) {
      try { await navigator.share({ files: [f], title: nombre || t('components.VisorDocumento.documento', 'Documento') }) } catch { /* cancelado */ }
      return
    }
    // Dentro de la app, una descarga volvería a meter el fichero en la vista: se lo damos al navegador del teléfono.
    if (enLaApp()) {
      setAvisoDescarga(t('components.VisorDocumento.abriendo_el_navegador_para_descargarlo', 'Abriendo el navegador para descargarlo…'))
      const r = await descargarFueraDeLaApp({ blob, nombre: nombre || 'documento' })
      setAvisoDescarga(r.ok ? null : r.error)
      return
    }
    const u = URL.createObjectURL(blob)
    descargarBlob(u, nombre || 'documento')
    setTimeout(() => URL.revokeObjectURL(u), 60000)
  }
  const verBoton = !!blobRef.current
  const boton = { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 999, border: 'none',
    background: 'rgba(255,255,255,0.16)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(6px)' }

  return (
    <div role="dialog" aria-modal="true" aria-label={nombre || t('components.VisorDocumento.documento', 'Documento')} className="visor-oscuro" data-visor-documento
      style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(8,10,14,0.94)', display: 'flex', flexDirection: 'column',
        paddingTop: 'max(10px, env(safe-area-inset-top))', paddingBottom: 'max(10px, var(--sa-abajo))', overscrollBehavior: 'contain' }}>
      {/* La barra: cerrar, el nombre y compartir. Siempre por debajo del notch. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px 10px', flexShrink: 0 }}>
        <button type="button" onClick={cerrar} aria-label={t('components.VisorDocumento.cerrar', 'Cerrar')} style={boton}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          {t('components.VisorDocumento.cerrar', 'Cerrar')}
        </button>
        <span style={{ flex: 1, minWidth: 0, color: '#fff', fontSize: 13.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nombre || t('components.VisorDocumento.documento', 'Documento')}
        </span>
        {verBoton && (
          <button type="button" onClick={compartir} style={boton}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            {puedeCompartir ? t('components.VisorDocumento.compartir', 'Compartir') : t('components.VisorDocumento.descargar', 'Descargar')}
          </button>
        )}
      </div>

      {avisoDescarga && <div role="status" style={{ color: '#fff', textAlign: 'center', fontSize: 13, padding: '0 12px 8px' }}>{avisoDescarga}</div>}
      {/* Lo que no son páginas (abriendo, el error, el vídeo, el audio, otro tipo de archivo): sin zoom, con su desplazamiento. */}
      {estado !== 'listo' && (
      <div style={{ flex: estado === 'cargando' ? '0 0 auto' : 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 4px' }}>
        {estado === 'cargando' && <div style={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', padding: 30, fontSize: 14 }}>{t('components.VisorDocumento.abriendo_el_documento', 'Abriendo el documento…')}</div>}
        {estado === 'error' && (
          <div style={{ color: '#fff', textAlign: 'center', padding: '30px 18px', fontSize: 14, lineHeight: 1.6 }}>
            {tx('components.VisorDocumento.no_se_ha_podido_abrir', 'No se ha podido abrir este documento aquí.{{x}}', { x: verBoton ? t('components.VisorDocumento.puedes_compartirlo_o_descargarlo_con', ' Puedes compartirlo o descargarlo con el botón de arriba.') : '' })}
          </div>
        )}
        {estado === 'video' && (
          <video src={src} controls playsInline autoPlay={false} onError={() => setEstado('noPdf')}
            style={{ display: 'block', width: '100%', maxHeight: 'calc(78vh / var(--z, 1))', margin: '0 auto', background: '#000', borderRadius: 6 }} />
        )}
        {estado === 'audio' && (
          <div style={{ padding: '30px 12px', display: 'flex', justifyContent: 'center' }}>
            <audio src={src} controls onError={() => setEstado('noPdf')} style={{ width: '100%', maxWidth: 520 }} />
          </div>
        )}
        {estado === 'noPdf' && (
          <div style={{ color: '#fff', textAlign: 'center', padding: '30px 18px', fontSize: 14, lineHeight: 1.6 }}>
            {tx('components.VisorDocumento.este_tipo_de_archivo_no', 'Este tipo de archivo no se puede ver dentro de la aplicación.{{x}}{{x2}}', { x: verBoton ? t('components.VisorDocumento.descargalo_para_abrirlo_con_otra', ' Descárgalo para abrirlo con otra app.') : '', x2: verBoton && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <button type="button" onClick={compartir} style={{ ...boton, background: 'rgba(255,255,255,0.22)' }}>
                  {puedeCompartir ? t('components.VisorDocumento.compartir', 'Compartir') : t('components.VisorDocumento.descargar', 'Descargar')} {nombre || t('components.VisorDocumento.el_archivo', 'el archivo')}
                </button>
              </div>
            ) })}
          </div>
        )}
      </div>
      )}
      {/* Las páginas: un lienzo por página, DENTRO DE LA ZONA AMPLIABLE (B-2254): dos dedos, doble toque, Ctrl+rueda y los
          botones − / +. Tiene su propio desplazamiento (el fondo no se mueve). Está desde que empieza a abrirse, para que
          las páginas tengan dónde ir. */}
      <div {...zona.propsExterior} style={{ ...zona.propsExterior.style, display: estado === 'listo' || estado === 'cargando' ? 'flex' : 'none' }}>
        <div {...zona.propsZona}>
          <div {...zona.propsMarco}>
            <div {...zona.propsContenido}>
              <div ref={contRef} />
              {paginas > MAX_PAGINAS && estado === 'listo' && (
                <div style={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', padding: '6px 18px 16px', fontSize: 12.5 }}>
                  {tx('components.VisorDocumento.se_ven_las_primeras_de', 'Se ven las primeras {{MAX_PAGINAS}} de {{paginas}} páginas.{{x}}', { MAX_PAGINAS, paginas, x: verBoton ? t('components.VisorDocumento.para_el_resto_compartelo_o', ' Para el resto, compártelo o descárgalo.') : '' })}
                </div>
              )}
            </div>
          </div>
        </div>
        {estado === 'listo' && <ControlesZoom zona={zona} />}
      </div>
    </div>
  )
}
